import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSimulation } from "@/context/SimulationContext";
import DashboardLayout from "@/components/DashboardLayout";
import { Users, Mail, Phone, Ticket, Download, Lock, MailPlus, CheckSquare, Square, UserPlus, ArrowUpDown, Upload, Pencil, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CampaignDialog } from "@/components/CampaignDialog";
import { CampaignHistory } from "@/components/CampaignHistory";
import { CustomerFormDialog } from "@/components/CustomerFormDialog";
import { ImportCustomersDialog } from "@/components/ImportCustomersDialog";
import ConfirmDialog from "@/components/ConfirmDialog";
import { toast } from "sonner";
import { effOf, isOwner } from "@/lib/perms";

const SORTS = [
  { v: "recent", label: "Más reciente" },
  { v: "numbers", label: "Más números comprados" },
  { v: "orders", label: "Más pedidos" },
];

export default function Customers() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { sim } = useSimulation();
  const feat = effOf(user, sim);
  const isFull = (!sim && user?.is_super_admin) || !!feat.can_download_csv;
  const isSuperAdmin = !sim && !!user?.is_super_admin;
  const canCampaigns = isSuperAdmin || (!sim && user?.role === "assistant" && !!user?.crm_access);
  const canCrm = isSuperAdmin || (sim ? true : isOwner(user)) || !!user?.crm_access;
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("recent");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [editCustomer, setEditCustomer] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [delCustomer, setDelCustomer] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [campaigns, setCampaigns] = useState([]);

  const loadCampaigns = useCallback(async () => {
    if (!canCampaigns) return;
    try { const res = await api.get("/campaigns"); setCampaigns(res.data); } catch (e) { /* sin historial */ }
  }, [canCampaigns]);

  const reload = useCallback(async () => {
    try { const res = await api.get("/customers"); setCustomers(res.data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!canCrm) { navigate("/dashboard", { replace: true }); return; }
    reload();
  }, [canCrm, navigate, reload]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/customers/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `clientes_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("No se pudo descargar el CSV");
    } finally { setDownloading(false); }
  };

  const downloadTemplate = () => {
    const csv = "nombre,correo,telefono\nMaría Rodríguez,maria@correo.com,88887777\n";
    const url = window.URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "plantilla_clientes.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const removeCustomer = async () => {
    setDeleting(true);
    try {
      await api.delete(`/customers/${delCustomer.id}`);
      setCustomers((prev) => prev.filter((x) => x.id !== delCustomer.id));
      setSelected((prev) => prev.filter((x) => x !== delCustomer.id));
      toast.success("Cliente eliminado del CRM");
      setDelCustomer(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar el cliente");
    } finally { setDeleting(false); }
  };

  const filtered = customers.filter((c) => {
    const s = q.toLowerCase();
    return !s || c.name?.toLowerCase().includes(s) || c.email?.toLowerCase().includes(s) || c.phone?.includes(s);
  }).sort((a, b) => {
    if (sort === "numbers") return (b.total_numbers || 0) - (a.total_numbers || 0);
    if (sort === "orders") return (b.total_orders || 0) - (a.total_orders || 0);
    return String(b.last_seen || "").localeCompare(String(a.last_seen || ""));
  });

  const PER_PAGE = 100;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const toggle = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const selectAll = () => setSelected(filtered.filter((c) => c.email).map((c) => c.id));
  const clearAll = () => setSelected([]);
  const recipients = customers.filter((c) => selected.includes(c.id));

  return (
    <DashboardLayout title="Clientes (CRM)">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <p className="text-sm text-neutral-500">Base de datos de tus compradores para campañas futuras.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap max-sm:w-full max-sm:grid max-sm:grid-cols-2">
          <Input data-testid="customer-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Buscar por nombre, correo o teléfono" className="rounded-xl max-w-xs max-sm:max-w-none max-sm:col-span-2 max-sm:h-11" />
          <div className="relative max-sm:col-span-2">
            <ArrowUpDown size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
            <select data-testid="customer-sort" value={sort} onChange={(e) => { setSort(e.target.value); setPage(1); }}
              className="h-10 rounded-xl border border-neutral-300 bg-white pl-8 pr-3 text-sm outline-none focus:border-[#00AEEF] max-sm:w-full max-sm:h-11">
              {SORTS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
            </select>
          </div>
          {canCrm && (
            <Button data-testid="add-customer-btn" onClick={() => setAddOpen(true)} variant="outline" className="rounded-xl gap-2 max-sm:h-11 max-sm:w-full">
              <UserPlus size={16} /> Agregar cliente
            </Button>
          )}
          {canCrm && (
            <Button data-testid="import-csv-btn" onClick={() => setImportOpen(true)} variant="outline" className="rounded-xl gap-2 max-sm:h-11 max-sm:w-full">
              <Upload size={16} /> Importar CSV
            </Button>
          )}
          {canCrm && (
            <button data-testid="download-template-btn" onClick={downloadTemplate}
              className="text-xs text-neutral-500 hover:text-[#0092CC] underline underline-offset-2 transition-colors max-sm:col-span-2 max-sm:py-1">
              Descargar plantilla CSV
            </button>
          )}
          {canCampaigns && (
            <Button data-testid="create-campaign-btn" onClick={() => setCampaignOpen(true)}
              className="rounded-xl gap-2 bg-[#059669] hover:bg-[#047857] text-white max-sm:h-11 max-sm:w-full">
              <MailPlus size={16} /> Crear Campaña
            </Button>
          )}
          {isFull ? (
            <Button data-testid="download-csv-btn" onClick={downloadCsv} disabled={downloading || customers.length === 0}
              className="rounded-xl gap-2 bg-[#00AEEF] hover:bg-[#0092CC] text-white max-sm:h-11 max-sm:w-full">
              <Download size={16} /> {downloading ? "Generando..." : "Descargar CSV"}
            </Button>
          ) : (
            <Button data-testid="download-csv-locked-btn" onClick={() => { toast.info("Disponible con Acceso Completo. Solicítalo desde tu panel."); navigate("/dashboard"); }}
              variant="outline" className="rounded-xl gap-2 text-neutral-500 border-dashed max-sm:h-11 max-sm:w-full">
              <Lock size={15} /> Descargar CSV
            </Button>
          )}
        </div>
      </div>

      {canCampaigns && filtered.length > 0 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Button data-testid="select-all-btn" onClick={selectAll} variant="outline" size="sm" className="rounded-xl gap-2 text-xs max-sm:h-11 max-sm:flex-1">
            <CheckSquare size={14} /> Seleccionar todos
          </Button>
          <Button data-testid="deselect-all-btn" onClick={clearAll} variant="outline" size="sm" className="rounded-xl gap-2 text-xs max-sm:h-11 max-sm:flex-1">
            <Square size={14} /> Deseleccionar todos
          </Button>
          <span data-testid="selected-count" className="text-xs text-neutral-500 max-sm:w-full max-sm:text-center">{selected.length} seleccionado(s)</span>
        </div>
      )}

      {loading ? (
        <p className="text-neutral-400">Cargando...</p>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-neutral-300 p-12 text-center">
          <div className="w-14 h-14 rounded-2xl bg-[#E6F7FE] text-[#00AEEF] flex items-center justify-center mx-auto mb-4"><Users size={26} /></div>
          <h3 className="font-display font-bold text-lg">Aún no hay clientes</h3>
          <p className="text-neutral-500 mt-1">Cuando alguien compre números, sus datos aparecerán aquí.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
          <table className="w-full text-sm hidden sm:table">
            <thead className="bg-neutral-50 text-neutral-500 text-left">
              <tr>
                {canCampaigns && <th className="px-4 py-3 w-10"></th>}
                <th className="px-4 py-3 font-medium">Nombre</th>
                <th className="px-4 py-3 font-medium hidden sm:table-cell"><Mail size={14} className="inline mr-1" />Correo</th>
                <th className="px-4 py-3 font-medium"><Phone size={14} className="inline mr-1" />Teléfono</th>
                <th className="px-4 py-3 font-medium text-center"><Ticket size={14} className="inline mr-1" />Números</th>
                <th className="px-4 py-3 font-medium text-center">Pedidos</th>
                {canCrm && <th className="px-4 py-3 w-12"></th>}
              </tr>
            </thead>
            <tbody>
              {paged.map((c) => (
                <tr key={c.id} className="border-t border-neutral-100" data-testid={`customer-row-${c.id}`}>
                  {canCampaigns && (
                    <td className="px-4 py-3">
                      <Checkbox data-testid={`customer-check-${c.id}`} checked={selected.includes(c.id)}
                        disabled={!c.email} onCheckedChange={() => toggle(c.id)} />
                    </td>
                  )}
                  <td className="px-4 py-3 font-medium">{c.name}</td>
                  <td className="px-4 py-3 text-neutral-500 hidden sm:table-cell">{c.email}</td>
                  <td className="px-4 py-3 text-neutral-500">{c.phone}</td>
                  <td className="px-4 py-3 text-center font-semibold">{c.total_numbers}</td>
                  <td className="px-4 py-3 text-center">{c.total_orders}</td>
                  {canCrm && (
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button data-testid={`edit-customer-${c.id}`} onClick={() => setEditCustomer(c)} title="Editar cliente"
                        className="w-8 h-8 rounded-lg text-neutral-400 hover:text-[#0092CC] hover:bg-[#E6F7FE] inline-flex items-center justify-center transition-colors">
                        <Pencil size={14} />
                      </button>
                      <button data-testid={`delete-customer-${c.id}`} onClick={() => setDelCustomer(c)} title="Eliminar cliente"
                        className="w-8 h-8 rounded-lg text-neutral-400 hover:text-red-500 hover:bg-red-50 inline-flex items-center justify-center transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Tarjetas apiladas en móvil */}
          <div className="sm:hidden divide-y divide-neutral-100">
            {paged.map((c) => (
              <div key={c.id} data-testid={`customer-card-${c.id}`} className="p-4">
                <div className="flex items-start gap-3">
                  {canCampaigns && (
                    <label className="pt-0.5 -m-2 p-2 shrink-0">
                      <Checkbox data-testid={`customer-check-m-${c.id}`} checked={selected.includes(c.id)}
                        disabled={!c.email} onCheckedChange={() => toggle(c.id)} className="h-5 w-5" />
                    </label>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate">{c.name}</p>
                    <p className="text-xs text-neutral-500 mt-1 flex items-center gap-1.5 break-all"><Mail size={12} className="shrink-0" />{c.email || "—"}</p>
                    <p className="text-xs text-neutral-500 mt-0.5 flex items-center gap-1.5"><Phone size={12} className="shrink-0" />{c.phone || "—"}</p>
                    <div className="flex items-center gap-2 mt-2.5">
                      <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-lg bg-[#E6F7FE] text-[#0092CC]">
                        <Ticket size={12} /> {c.total_numbers} números
                      </span>
                      <span className="text-xs font-medium px-2 py-1 rounded-lg bg-neutral-100 text-neutral-600">
                        {c.total_orders} pedido(s)
                      </span>
                    </div>
                  </div>
                  {canCrm && (
                    <div className="flex flex-row gap-1.5 shrink-0">
                      <button data-testid={`edit-customer-m-${c.id}`} onClick={() => setEditCustomer(c)} aria-label="Editar cliente"
                        className="w-11 h-11 rounded-xl border border-neutral-200 text-neutral-500 active:bg-[#E6F7FE] active:text-[#0092CC] flex items-center justify-center transition-colors">
                        <Pencil size={17} />
                      </button>
                      <button data-testid={`delete-customer-m-${c.id}`} onClick={() => setDelCustomer(c)} aria-label="Eliminar cliente"
                        className="w-11 h-11 rounded-xl border border-neutral-200 text-neutral-500 active:bg-red-50 active:text-red-500 flex items-center justify-center transition-colors">
                        <Trash2 size={17} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-3 border-t border-neutral-100 bg-neutral-50 max-sm:flex-col">
            <p className="text-xs text-neutral-500 max-sm:text-center" data-testid="customer-pagination-info">
              Mostrando <b>{(currentPage - 1) * PER_PAGE + 1}</b>–<b>{Math.min(currentPage * PER_PAGE, filtered.length)}</b> de <b>{filtered.length}</b> cliente(s)
              {filtered.length !== customers.length && <> (de {customers.length} en total)</>}
            </p>
            <div className="flex items-center gap-2 max-sm:w-full max-sm:justify-between">
              <Button data-testid="prev-page-btn" onClick={() => setPage(currentPage - 1)} disabled={currentPage <= 1}
                variant="outline" size="sm" className="rounded-xl gap-1 text-xs max-sm:h-11 max-sm:flex-1">
                <ChevronLeft size={14} /> Anterior
              </Button>
              <span className="text-xs text-neutral-500 whitespace-nowrap" data-testid="customer-page-label">Página {currentPage} de {totalPages}</span>
              <Button data-testid="next-page-btn" onClick={() => setPage(currentPage + 1)} disabled={currentPage >= totalPages}
                variant="outline" size="sm" className="rounded-xl gap-1 text-xs max-sm:h-11 max-sm:flex-1">
                Siguiente <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        </div>
      )}

      {canCampaigns && (
        <>
          <CampaignHistory campaigns={campaigns} onRefresh={loadCampaigns} />
          <CampaignDialog open={campaignOpen} onOpenChange={setCampaignOpen} recipients={recipients}
            onSent={() => { clearAll(); setTimeout(loadCampaigns, 800); }} />
        </>
      )}
      <CustomerFormDialog open={addOpen || !!editCustomer}
        onOpenChange={(v) => { if (!v) { setAddOpen(false); setEditCustomer(null); } }}
        customer={editCustomer}
        onSaved={(c, wasEditing) => setCustomers((prev) => wasEditing
          ? prev.map((x) => x.id === c.id ? { ...x, ...c } : x)
          : [c, ...prev])} />
      <ImportCustomersDialog open={importOpen} onOpenChange={setImportOpen} onImported={reload} />
      <ConfirmDialog
        open={!!delCustomer}
        onOpenChange={(v) => { if (!v) setDelCustomer(null); }}
        title="¿Estás seguro de que deseas eliminar este cliente del CRM?"
        description="Esta acción no se puede deshacer."
        confirmText="Sí, eliminar"
        loading={deleting}
        onConfirm={removeCustomer}
      />
    </DashboardLayout>
  );
}
